import React, {ErrorInfo, PropsWithChildren} from 'react'
import {ThemeProvider} from '@mui/material/styles'
import {Provider, useSelector} from 'react-redux';
import {criticalErrorBlockSaveState, requestRemoveAppStateFromLocalStorage} from 'util/redux/ReduxStateUtil';
import {SessionInfoModal} from 'module/SessionInfo/component/modal/SessionInfoModal';
import {TopNavBar} from 'module/TopNavBar/component/TopNavBar';
import Portal from '@mui/material/Portal';
import {NotiDrawer} from 'module/Noti/component/NotiDrawer';
import {sendErrorReportViaEMail} from 'util/sendErrorReportViaEMail';
import {selTheme} from 'module/Theme/selector/ThemeSelector';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {ConfirmDialogContainer} from 'module/ConfirmDialog/component/ConfirmDialogContainer';
import ReactDOM from 'react-dom';
import {log} from 'common/log';
import {Store} from 'redux';
import {NetworkStatusComponent} from 'component/NetworkStatusComponent'

function ThemedContent({isRootContent, children}: PropsWithChildren<{ isRootContent: boolean }>) {
	const theme = useSelector(selTheme);
	return (
		<ThemeProvider theme={theme}>
			{isRootContent &&
				<Portal container={document.getElementById('headerContainer')}>
					<TopNavBar/>
					<NetworkStatusComponent/>
				</Portal>
			}
			{children}
			{isRootContent && <>
				<NotiDrawer/>
				<SessionInfoModal/>
				<ToastContainer theme={'colored'}/>
				<ConfirmDialogContainer/>
			</>}
		</ThemeProvider>
	);
}

let greenButtonStyle = {borderRadius: 5, borderColor: 'black', background: '#6aff6a', color: 'black', padding: 10};
let yellowButtonStyle = {...greenButtonStyle, background: '#feff2e'};
let redButtonStyle = {...greenButtonStyle, background: '#ea0000', color: 'white'};

interface AppBoundaryProps {
	store: Store,
	isAdditionalContent?: boolean
}

interface AppBoundaryState {
	panic: boolean
	error?: Error
	info?: ErrorInfo
}

export class AppBoundary extends React.Component<PropsWithChildren<AppBoundaryProps>, AppBoundaryState> {
	constructor(props: AppBoundaryProps) {
		super(props)
		this.state = {panic: false}
	}

	static getDerivedStateFromError(error: any) {
		// Update state so the next render will show the fallback UI.
		return {
			panic: true,
		}
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		// You can also log the error to an error reporting service
		log.error(error, info)
		criticalErrorBlockSaveState()
		this.setState({error, info})
	}

	render() {
		if (this.state.panic) {
			const {error, info} = this.state
			const stack = error && error.stack && error.stack.toString()
			const componentStack = info && info.componentStack && info.componentStack.toString()
			return ReactDOM.createPortal(
				<div style={{
					top: 0,
					left: 0,
					position: 'fixed',
					overflow: 'auto',
					textAlign: 'center',
					height: '100%',
					width: '100%',
					zIndex: 999999999,
					background: '#f1f1f1',
				}}>
					<h1>Applikationsfehler</h1>
					<div>Fehler beim Aufbau der Seite</div>
					<p>
						<button style={yellowButtonStyle} onClick={() => sendErrorReportViaEMail(stack || error, componentStack)}>
							Fehler per E-Mail melden
						</button>
					</p>
					<p>
						<button style={greenButtonStyle} onClick={() => window.location.reload()}>
							Seite neu laden
						</button>
					</p>
					<p>
						<button style={redButtonStyle} onClick={requestRemoveAppStateFromLocalStorage}>
							Lokale Einstellungen zurücksetzen
							<br/>
							und Seite neu laden
						</button>
					</p>
				</div>,
				window.document.body,
			)
		} else {
			const {store, isAdditionalContent = false} = this.props
			return (
				<Provider store={store}>
					<ThemedContent isRootContent={!isAdditionalContent}>
						{this.props.children}
					</ThemedContent>
				</Provider>
			);
		}
	}
}